import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Pagination, Alert, Spinner, Badge, Tabs, Tab, Collapse, Modal } from 'react-bootstrap';
import { FaCalendarAlt, FaSync } from 'react-icons/fa';
import apiService, { makeAuthenticatedRequest } from '../services/apiService';
import Breadcrumb from '../components/Breadcrumb';
import 'bootstrap/dist/css/bootstrap.min.css';
import './test.css';
import './FileCommissions.css';

// API base URL for direct requests
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/api";

const FileCommissions = () => {
  // State management
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  // Summary lists directly from API (not derived from commissions)
  const [processors, setProcessors] = useState([]);
  const [transactionCoordinators, setTransactionCoordinators] = useState([]);
  // Sales persons summary list
  const [salespersons, setSalespersons] = useState([]);
  const [salespersonsLoading, setSalespersonsLoading] = useState(false);
  const [salespersonsError, setSalespersonsError] = useState(null);
  // Managers summary list
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [managersError, setManagersError] = useState(null);

  // Filter state
  const [dateFilter, setDateFilter] = useState('current_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [month, setMonth] = useState('');
  const [salesPersonName, setSalesPersonName] = useState('');
  const [transactionCoordinator, setTransactionCoordinator] = useState('');
  const [processor, setProcessor] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showSummaries, setShowSummaries] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    dateFilter: 'current_month',
    startDate: '',
    endDate: '',
    month: '',
    salesPersonName: '',
    transactionCoordinator: '',
    processor: ''
  });

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [forceGenerating, setForceGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [selectedGenerateMonth, setSelectedGenerateMonth] = useState('');
  const [exportingCsv, setExportingCsv] = useState(false);

  // Initialize current month
  useEffect(() => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setMonth(currentMonth);
    setSelectedGenerateMonth(currentMonth);
    setAppliedFilters((prev) => ({ ...prev, month: currentMonth }));
  }, []);

  // Fetch commissions data
  const fetchCommissions = async (page = 1, filters = appliedFilters) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getFileCommissions(page, pageSize, filters);

      if (response && (response.results || Array.isArray(response))) {
        const rows = response.results || response;
        const count = response.count ?? response.total_count ?? rows.length;
        setCommissions(rows);
        setTotalCount(count);
        setTotalPages(Math.ceil(count / pageSize) || 1);
        // Populate summary lists directly from API if provided
        setProcessors(response.processors || []);
        setTransactionCoordinators(response.transaction_coordinators || []);
      } else {
        setCommissions([]);
        setTotalCount(0);
        setTotalPages(1);
        setProcessors([]);
        setTransactionCoordinators([]);
      }
    } catch (err) {
      console.error('Error fetching file commissions:', err);
      setError('Failed to load commissions. Please try again.');
      setCommissions([]);
      setTotalCount(0);
      setTotalPages(1);
      setProcessors([]);
      setTransactionCoordinators([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchCommissions(currentPage, appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, appliedFilters]);

  // Handle filter application
  const handleApplyFilters = () => {
    const newFilters = { dateFilter, startDate, endDate, month, salesPersonName, transactionCoordinator, processor };
    setAppliedFilters(newFilters);
    setCurrentPage(1);
  };

  // Handle filter reset
  const handleResetFilters = () => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    setDateFilter('current_month');
    setStartDate('');
    setEndDate('');
    setMonth(currentMonth);
    setSalesPersonName('');
    setTransactionCoordinator('');
    setProcessor('');

    const resetFilters = {
      dateFilter: 'current_month',
      startDate: '',
      endDate: '',
      month: currentMonth,
      salesPersonName: '',
      transactionCoordinator: '',
      processor: ''
    };
    setAppliedFilters(resetFilters);
    setCurrentPage(1);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchCommissions(currentPage, appliedFilters);
  };

  const handleOpenGenerateModal = () => {
    setGenerateError(null);
    setShowGenerateModal(true);
  };

  const handleCloseGenerateModal = () => {
    if (!forceGenerating) setShowGenerateModal(false);
  };

  const getMonthRange = (ym) => {
    if (!ym) return null;
    const [yStr, mStr] = ym.split('-');
    const y = Number(yStr);
    const m = Number(mStr);
    if (!y || !m) return null;
    const start = new Date(Date.UTC(y, m - 1, 1));
    const end = new Date(Date.UTC(y, m, 0));
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10)
    };
  };

  const handleRunGenerate = async () => {
    setGenerateError(null);
    const range = getMonthRange(selectedGenerateMonth);
    if (!range) {
      setGenerateError('Please select a valid month.');
      return;
    }
    try {
      setForceGenerating(true);
      const url = `${API_BASE_URL}/automation/closed-orders-commission-runner/?start=${range.start}&end=${range.end}`;
      const response = await makeAuthenticatedRequest(url);
      if (!response.ok) {
        setGenerateError(`HTTP error ${response.status}`);
      } else {
        const newFilters = {
          dateFilter: 'month',
          startDate: '',
          endDate: '',
          month: selectedGenerateMonth,
          salesPersonName,
          transactionCoordinator,
          processor
        };
        setDateFilter('month');
        setStartDate('');
        setEndDate('');
        setMonth(selectedGenerateMonth);
        setAppliedFilters(newFilters);
        setCurrentPage(1);
        await fetchCommissions(1, newFilters);
        setShowGenerateModal(false);
      }
    } catch (err) {
      setGenerateError(err.message || 'Request failed');
    } finally {
      setForceGenerating(false);
    }
  };

  // Export CSV (up to 500 records, respects current filters)
  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const response = await apiService.getFileCommissions(1, 500, appliedFilters);
      const rows = response.results || response || [];
      if (!Array.isArray(rows) || rows.length === 0) {
        // eslint-disable-next-line no-alert
        alert('No records found to export.');
        return;
      }

      const headers = [
        'Order Number',
        'Sales Persons',
        'Allocation',
        'Transaction Type',
        'Commission per Sales person',
        'Agent Policy Premium',
        'Purchase Price',
        'Loan Amount',
        'Order Open Date',
        'Order Closed Date',
        'Address',
        'Transaction Coordinator',
        'Processor',
        'Underwriter Name'
      ];

      const toCsvValue = (val) => {
        if (val === null || val === undefined) return '';
        const s = String(val).replace(/\n/g, ' ').replace(/\r/g, ' ').trim();
        if (s.includes(',') || s.includes('"')) {
          return '"' + s.replace(/"/g, '""') + '"';
        }
        return s;
      };

      const lines = [headers.join(',')];

      rows.forEach((c) => {
        const commissionPerSp = Array.isArray(c.salesperson_entries) && c.salesperson_entries.length > 0
          ? c.salesperson_entries.map(sp => `${sp.sales_person_name || sp.name || ''}: ${formatCurrency(sp.commission_amount)} (${formatPercent(sp.commission_rate)})${sp.is_bonus_applied ? ' HiLi' : ''}`).join(' | ')
          : (Array.isArray(c.sales_person_data) && c.sales_person_data.length > 0
            ? c.sales_person_data.map(sp => `${sp.sales_person_name || sp.name || ''}: ${sp.commission_amount !== undefined ? formatCurrency(sp.commission_amount) : ''}${sp.commission_rate !== undefined ? ` (${formatPercent(sp.commission_rate)})` : ''}`).join(' | ')
            : formatCurrency(c.commission_per_sales_person));

        const row = [
          toCsvValue(c.order_number || 'N/A'),
          toCsvValue(getSalesPersonNames(c)),
          toCsvValue(formatAllocation(c)),
          toCsvValue(c.transaction_type || 'N/A'),
          toCsvValue(commissionPerSp),
          toCsvValue(formatCurrency(c.total_net)),
          toCsvValue(formatCurrency(c.purchase_price)),
          toCsvValue(formatCurrency(c.loan_amount)),
          toCsvValue(formatDate(c.order_open_date)),
          toCsvValue(formatDate(c.order_closed_date)),
          toCsvValue(formatAddress(c)),
          toCsvValue(c.transaction_coordinator || 'N/A'),
          toCsvValue(c.processor || 'N/A'),
          toCsvValue(c.underwriter_name || 'N/A')
        ];
        lines.push(row.join(','));
      });

      const csvContent = lines.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0,19).replace(/[:T]/g,'-');
      link.download = `commissions_export_${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export CSV failed:', e);
      // eslint-disable-next-line no-alert
      alert(e.message || 'Could not export CSV');
    } finally {
      setExportingCsv(false);
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
const breadcrumbItems = [
      { label: 'Commissions', href: null},
      { label: 'Reports', href: null},
      { label: 'Commission Report', href: null }
  ];
  // Format date (force UTC calendar day, avoid +/-1 day shifts)
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Handle pure date strings (YYYY-MM-DD) explicitly
      const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(dateString);
      if (m) {
        const [, y, mo, d] = m;
        const utcDate = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
        const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${monthNames[utcDate.getUTCMonth()]} ${String(utcDate.getUTCDate()).padStart(2,'0')}, ${utcDate.getUTCFullYear()}`;
      }

      // ISO datetime or other parseable formats: compute from UTC components
      const d = new Date(dateString);
      if (Number.isNaN(d.getTime())) return dateString;
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const day = String(d.getUTCDate()).padStart(2,'0');
      const month = monthNames[d.getUTCMonth()];
      const year = d.getUTCFullYear();
      return `${month} ${day}, ${year}`;
    } catch (e) {
      return dateString;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    const num = Number(amount);
    if (Number.isNaN(num)) return '$0.00';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format percent from a decimal (e.g., 0.12 => 12%)
  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const num = Number(value);
    if (Number.isNaN(num)) return 'N/A';
    const pct = num * 100;
    const rounded = Math.abs(pct % 1) < 1e-9 ? pct.toFixed(0) : pct.toFixed(2);
    return `${rounded}%`;
  };

  // Combine closing address into a single column
  const formatAddress = (c) => {
    const parts = [];
    if (c.closing_location_address1) parts.push(c.closing_location_address1);
    if (c.closing_location_address2) parts.push(c.closing_location_address2);
    const cityStateZip = [c.closing_location_city, c.closing_location_state, c.closing_location_zipcode]
      .filter(Boolean)
      .join(', ');
    if (cityStateZip) parts.push(cityStateZip);
    // if (c.closing_location_county) parts.push(c.closing_location_county);
    return parts.length ? parts.join(' • ') : 'N/A';
  };

  // Extract salesperson names as comma-separated list
  const getSalesPersonNames = (c) => {
    if (Array.isArray(c.settlement_team_contacts)) {
      const names = c.settlement_team_contacts
        .filter((ct) => (ct.role || '').toLowerCase() === 'sales person' && ct.name)
        .map((ct) => ct.name);
      if (names.length) return names.join(', ');
    }
    if (Array.isArray(c.sales_persons) && c.sales_persons.length) {
      const names = c.sales_persons
        .map((sp) => {
          if (!sp) return '';
          if (typeof sp === 'string') return sp;
          return sp.name || sp.full_name || sp.display_name || '';
        })
        .filter(Boolean);
      if (names.length) return names.join(', ');
    }
    return 'N/A';
  };

  // Format allocation percentage (100% divided by sales_person_count)
  const formatAllocation = (c) => {
    const count = Number(c.sales_person_count) || 0;
    const pct = count > 0 ? (100 / count) : 100;
    const rounded = pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(2);
    return `${rounded}%`;
  };

  // Get filter description
  const getFilterDescription = () => {
    const { dateFilter, startDate, endDate, month } = appliedFilters;
    if (dateFilter === 'current_month') {
      return 'Current Month';
    } else if (dateFilter === 'month' && month) {
      const monthDate = new Date(month + '-01');
      return monthDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    } else if (dateFilter === 'date_range' && startDate && endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    return 'Current Month';
  };

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    return (
      <Pagination className="justify-content-center mt-3">
        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

        {startPage > 1 && (
          <>
            <Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>
            {startPage > 2 && <Pagination.Ellipsis />}
          </>
        )}

        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
          <Pagination.Item key={page} active={page === currentPage} onClick={() => handlePageChange(page)}>
            {page}
          </Pagination.Item>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <Pagination.Ellipsis />}
            <Pagination.Item onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>
          </>
        )}

        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
      </Pagination>
    );
  };

  // Fetch sales persons list (summary) respecting current filters
  const fetchSalesPersons = async (filters = appliedFilters) => {
    try {
      setSalespersonsLoading(true);
      setSalespersonsError(null);

      const params = new URLSearchParams({
        page: '1',
        limit: '100',
      });

      // Date filters
      if (filters?.dateFilter === 'month' && filters?.month) {
        params.append('month', filters.month);
      } else if (filters?.dateFilter === 'date_range' && filters?.startDate && filters?.endDate) {
        params.append('start_date', filters.startDate);
        params.append('end_date', filters.endDate);
      }

      // Member filters
      if (filters?.processor) params.append('processor', filters.processor);
      if (filters?.transactionCoordinator) params.append('transaction_coordinator', filters.transactionCoordinator);

      const response = await makeAuthenticatedRequest(
        `${API_BASE_URL}/automation/salespersons/?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setSalespersons(data.results || []);
    } catch (err) {
      console.error('Error fetching salespersons summary:', err);
      setSalespersonsError('Failed to load sales persons summary.');
      setSalespersons([]);
    } finally {
      setSalespersonsLoading(false);
    }
  };

  // Keep sales persons summary in sync with filters
  useEffect(() => {
    fetchSalesPersons(appliedFilters);
    // Also fetch managers (no date filters server-side, used for frontend calc)
    const fetchManagers = async () => {
      try {
        setManagersLoading(true);
        setManagersError(null);
        const data = await apiService.getManagersManage();
        setManagers(Array.isArray(data) ? data : (data.results || []));
      } catch (err) {
        console.error('Error fetching managers:', err);
        setManagersError('Failed to load managers.');
        setManagers([]);
      } finally {
        setManagersLoading(false);
      }
    };
    fetchManagers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  return (
    <Container fluid className="px-4 py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <style jsx>{`
        .table-modern thead th {padding:11px 0.8rem;font-weight:800}
        .page-title { color: #2c3e50; font-weight: 700; font-size: 2rem; }
        .page-subtitle { font-size: 1rem; color: #6c757d; }
        .main-card { border: none; border-radius: 20px; box-shadow: 0 10px 40px rgba(0,0,0,0.1); }
        .card-header-custom { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border: none; padding: 1.25rem 1.5rem; }
        .filter-controls { background: rgba(255,255,255,0.95); border-radius: 12px; padding: 1rem; box-shadow: 0 4px 20px rgba(0,0,0,0.08); border: 1px solid rgba(255,255,255,0.2); }
        .table-modern { display: inline-table; table-layout: auto; width: auto; margin-bottom: 0; border-collapse: separate; }
        // .table-modern thead th { background-color:transparent !important; border: none; font-weight: 600; color: #495057; padding: 12px 15px; font-size: 0.85rem; text-transform: uppercase; border-bottom: 2px solid #e9ecef; white-space: nowrap; }
        .table-modern tbody td { font-size:0.85rem;padding: 12px 15px; border: none; vertical-align: middle; border-bottom: 1px solid #f1f3f4; white-space: nowrap; }
        .table-modern thead th, .table-modern tbody td { box-sizing: border-box; }
        /* Per-column min-widths tuned to current dataset */
        .table-modern thead th, .table-modern tbody td { box-sizing: border-box; }
        .table-modern thead th:nth-child(1), .table-modern tbody td:nth-child(1) { min-width: 140px; } /* Order Number */
        .table-modern thead th:nth-child(2), .table-modern tbody td:nth-child(2) { min-width: 300px; width: max-content; } /* Sales Persons */
        .table-modern thead th:nth-child(3), .table-modern tbody td:nth-child(3) { min-width: 90px; } /* Allocation */
        .table-modern thead th:nth-child(4), .table-modern tbody td:nth-child(4) { min-width: 200px; } /* c% */
        .table-modern thead th:nth-child(5), .table-modern tbody td:nth-child(5) { min-width: 350px; } /* Commission per Sales person */
        .table-modern thead th:nth-child(6), .table-modern tbody td:nth-child(6) { min-width: 180px; } /* OverAll Commission */
        .table-modern thead th:nth-child(7), .table-modern tbody td:nth-child(7) { min-width: 220px; } /* Agent Policy Premium */
        .table-modern thead th:nth-child(8), .table-modern tbody td:nth-child(8) { min-width: 150px; } /* Purchase Price */
        .table-modern thead th:nth-child(9), .table-modern tbody td:nth-child(9) { min-width: 150px; } /* Loan Amount */
        .table-modern thead th:nth-child(10), .table-modern tbody td:nth-child(10) { min-width: 200px; } /* Order Open Date */
        .table-modern thead th:nth-child(11), .table-modern tbody td:nth-child(11) { min-width: 200px; } /* Order Closed Date */
        .table-modern thead th:nth-child(12), .table-modern tbody td:nth-child(12) { min-width: 450px; width: max-content; } /* Address */
        .table-modern thead th:nth-child(13), .table-modern tbody td:nth-child(13) { min-width: 250px; } /* Transaction Coordinator */
        .table-modern thead th:nth-child(14), .table-modern tbody td:nth-child(14) { min-width: 180px; } /* Processor */
        .table-modern thead th:nth-child(16), .table-modern tbody td:nth-child(15) { min-width: 350px; } /* Underwriter Name */
        .loading-container { padding: 3rem 2rem; text-align: center; }
        .table-scroll { width: 100%; display: block; overflow-x: auto !important; overflow-y: hidden; -webkit-overflow-scrolling: touch; }
        /* Modal: fit width to content */
        .modal-fit-content { max-width: fit-content !important; }
        .modal-fit-content .modal-content { width: fit-content; }
        @media (max-width: 576px) { .modal-fit-content { max-width: 95vw !important; } }
        /* Modal subtitle styling - visible on light headers */
        .modal-subtitle { font-size: 0.95rem; color: #6c757d; margin-top: 0.25rem; }
        .modal-header .modal-title { margin-bottom: 0; }
        /* Generate Report button - outline styling */
        .generate-report-btn {
          background: transparent;
          border-color: rgb(229, 83, 83);
          color: rgb(229, 83, 83);
        }
        .generate-report-btn:hover, .generate-report-btn:focus {
          background: rgba(229, 83, 83, 0.08);
          border-color: rgb(219, 73, 73);
          color: rgb(219, 73, 73);
        }
      `}</style>

      <Breadcrumb items={breadcrumbItems} />

      {/* Page Header Section */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="page-title mb-1">Commissions</h2>
              <p className="page-subtitle text-muted mb-0">View commission records</p>
            </div>
            <div className="d-flex align-items-center gap-3">
              <div className="btn-group" role="group">
                 <Button 
          variant={showFilters ? "primary" : "outline-primary"}
          onClick={() => setShowFilters(!showFilters)}
          className="d-flex align-items-center gap-2"
        >
          {showFilters ? 'Filters' : 'Filters'}           <i className="fas fa-filter"></i>

        </Button>

              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Modal show={showGenerateModal} onHide={handleCloseGenerateModal} centered >
        <Modal.Header closeButton>
          <div className="w-100">
            <Modal.Title>Generate Monthly Report</Modal.Title>
            <div className="modal-subtitle">with Estimated Closings</div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {generateError && (
            <Alert variant="danger" className="mb-3">{generateError}</Alert>
          )}
          {forceGenerating && (
            <Alert variant="info" className="mb-3 d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              This may take a few minutes. Please stay with us.
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Select Month</Form.Label>
            <Form.Control
              type="month"
              value={selectedGenerateMonth}
              onChange={(e) => setSelectedGenerateMonth(e.target.value)}
            />
          </Form.Group>
          {(() => {
            const r = getMonthRange(selectedGenerateMonth);
            return r ? (
              <div className="small text-muted">Start: {formatDate(r.start)} • End: {formatDate(r.end)}</div>
            ) : null;
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseGenerateModal} disabled={forceGenerating}>Cancel</Button>
          <Button variant="primary" onClick={handleRunGenerate} disabled={forceGenerating || !selectedGenerateMonth}>
            {forceGenerating ? <Spinner animation="border" size="sm" /> : null}
            <span className="ms-2">Generate</span>
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Filter Section */}
      {showFilters && (
        <Card className="mb-4 filter-card" style={{ border: 'none', boxShadow: '0 8px 25px rgba(0,0,0,0.12)', borderRadius: '16px' }}>
          <Card.Header className="card-header-custom">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                                <i className="fas fa-sliders-h me-2"></i>
                <h6 className="mb-0 fw-bold">Filters</h6>
              </div>
            </div>
          </Card.Header>
          <Card.Body style={{ padding: '1rem' }}>
            <Row className="align-items-center g-4 mb-2">
              <Col lg={3}>
                <div className="filter-controls">
                  <label className="form-label fw-semibold text-muted mb-2">Filter Duration</label>
                  <Form.Select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                    <option value="current_month">Current Month</option>
                    <option value="month">Specific Month</option>
                    <option value="date_range">Date Range</option>
                  </Form.Select>
                </div>
              </Col>

              {dateFilter === 'month' && (
                <Col lg={3}>
                  <div className="filter-controls">
                    <label className="form-label fw-semibold text-muted mb-2">Month</label>
                    <Form.Control type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
                  </div>
                </Col>
              )}

              {dateFilter === 'date_range' && (
                <>
                  <Col lg={3}>
                    <div className="filter-controls">
                      <label className="form-label fw-semibold text-muted mb-2">Start Date</label>
                      <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="filter-controls">
                      <label className="form-label fw-semibold text-muted mb-2">End Date</label>
                      <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                  </Col>
                </>
              )}

              {/* New name-based filters */}
              <Col lg={3}>
                <div className="filter-controls">
                  <label className="form-label fw-semibold text-muted mb-2">Sales Person Name</label>
                  <Form.Select value={salesPersonName} onChange={(e) => setSalesPersonName(e.target.value)}>
                    <option value="">All Sales Persons</option>
                    <option value="Elizabeth Caulfield">Elizabeth Caulfield</option>
                    <option value="Rachel Rau">Rachel Rau</option>
                    <option value="Samantha Lyons">Samantha Lyons</option>
                    <option value="Tricia Magrann">Tricia Magrann</option>
                    <option value="Quandell Iglesia">Quandell Iglesia</option>
                    <option value="William Gordon">William Gordon</option>
                    <option value="Kristen Marrone">Kristen Marrone</option>
                  </Form.Select>
                </div>
              </Col>
              <Col lg={3}>
                <div className="filter-controls">
                  <label className="form-label fw-semibold text-muted mb-2">Transaction Coordinator</label>
                  <Form.Select value={transactionCoordinator} onChange={(e) => setTransactionCoordinator(e.target.value)}>
                    <option value="">All Transaction Coordinators</option>
                    <option value="Whitney Cox">Whitney Cox</option>
                    <option value="Annette Vogdes">Annette Vogdes</option>
                    <option value="Natalie Colon">Natalie Colon</option>
                    <option value="JoAnna Fahey">JoAnna Fahey</option>
                  </Form.Select>
                </div>
              </Col>
              <Col lg={3}>
                <div className="filter-controls">
                  <label className="form-label fw-semibold text-muted mb-2">Processor</label>
                  <Form.Select value={processor} onChange={(e) => setProcessor(e.target.value)}>
                    <option value="">All Processors</option>
                    <option value="Kelsey Palladino">Kelsey Palladino</option>
                    <option value="Lea Coyle">Lea Coyle</option>
                    <option value="Mia Blagman">Mia Blagman</option>
                  </Form.Select>
                </div>
              </Col>
            </Row>

            {/* Filter Actions */}
            <Row className="mt-0 pt-3 pb-2" style={{ borderTop: '1px solid #e9ecef', minHeight: '60px' }}>
              <Col>
                <div className="d-flex align-items-center justify-content-end flex-wrap gap-3 h-100">
                  <Button variant="primary" className="btn-modern" onClick={handleApplyFilters}>Apply</Button>
                  <Button variant="outline-secondary" className="btn-modern" onClick={handleResetFilters}>Reset</Button>
                  
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Summary Tabs: Sales Persons, Managers, Processors, Coordinators */}
      <Card className="main-card mb-4">
        <Card.Header className="card-header-custom d-flex justify-content-between align-items-center">
          <span className="fw-semibold">Production & Sales teams</span>
          <Button
            variant="light"
            className={`${showSummaries ? 'btn-white-active' : 'btn-outline-white'} d-flex align-items-center gap-2`}
            onClick={() => setShowSummaries(!showSummaries)}
            aria-expanded={showSummaries}
            aria-controls="summary-collapse"
          >
            <i className={`fas ${showSummaries ? 'fa-eye' : 'fa-eye-slash'}`}></i>
            <span>Teams</span>
          </Button>
        </Card.Header>
        <Collapse in={showSummaries}>
          <div id="summary-collapse">
            <Card.Body className="pt-3">
      <Tabs defaultActiveKey="salespersons" id="summary-tabs" className="super-mario-tabs nav-tabs card-header-tabs mb-3" justify mountOnEnter unmountOnExit>
        <Tab eventKey="salespersons" title={<span>Sales Persons <span className="text-muted small">({salespersons.length})</span></span>}>
          {/* Totals bar */}
          {!salespersonsLoading && !salespersonsError && salespersons.length > 0 && (
            <div className="d-flex flex-wrap gap-4 mb-3 small text-muted">
              
              <span className="d-flex align-items-center gap-2">
                Total Commissions: <span className="fw-semibold text-dark">{formatCurrency(salespersons.reduce((acc, sp) => acc + Number(sp.total_commissions || 0), 0))}</span>
              </span>
            </div>
          )}
          {salespersonsLoading && (
            <div className="loading-container">
              <Spinner animation="border" variant="primary" />
            </div>
          )}
          {!salespersonsLoading && salespersonsError && (
            <Alert variant="danger" className="mt-3">{salespersonsError}</Alert>
          )}
          {!salespersonsLoading && !salespersonsError && (
            salespersons.length > 0 ? (
              <Row className="mb-4">
                <Col md={12} className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                      Sales Persons
                    </h6>
                    <span className="text-muted small">{salespersons.length}</span>
                  </div>
                  <Row xs={1} sm={3} md={5} className="g-2">
                    {salespersons.map((sp) => (
                      <Col key={`salesperson-${sp.id}`}>
                        <div className="agent-card p-3" aria-label={`Sales Person ${sp.name}`}>
                          {/* Header */}
                          <div className="d-flex justify-content-between align-items-center mb-4">
                            <div className="d-flex align-items-center gap-3">
                              <div className="avatar-gradient">
                                <i className="fas fa-fw fa-user-tie text-white fs-5"></i>
                              </div>
                              <div>
                                <h6 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                                  {sp.name}
                                </h6>
                              </div>
                            </div>
                          </div>

                          {/* Body */}
                          <div className="card-body p-0">
                            <div className="small text-muted">
                              <div className="d-flex justify-content-between py-1 border-top">
                                <span>Total Closings</span>
                                <span className="fw-semibold text-dark">{Number(sp.commission_count || 0)}</span>
                              </div>
                              <div className="d-flex justify-content-between py-1 border-top">
                                <span>Total Commissions</span>
                                <span className="fw-semibold text-dark">{formatCurrency(sp.total_commissions)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Col>
              </Row>
            ) : (
              <div className="text-muted small">No sales person data available.</div>
            )
          )}
        </Tab>
        <Tab eventKey="managers" title={<span>Managers <span className="text-muted small">({managers.length})</span></span>}>
          {/* Totals bar for Managers */}
          {!managersLoading && !managersError && managers.length > 0 && (
            <div className="d-flex flex-wrap gap-4 mb-3 small text-muted">
              <span className="d-flex align-items-center gap-2">
                Total Agent Policy Premium: <span className="fw-semibold text-dark">{formatCurrency(commissions.reduce((acc, c) => acc + Number(c.total_net || 0), 0))}</span>
              </span>
              <span className="d-flex align-items-center gap-2">
                Total Managers Commissions: <span className="fw-semibold text-dark">{formatCurrency(managers.reduce((acc, m) => acc + (Number(m.base_commission || 0) * commissions.reduce((s, c) => s + Number(c.total_net || 0), 0)), 0))}</span>
              </span>
            </div>
          )}
          {managersLoading && (
            <div className="loading-container">
              <Spinner animation="border" variant="primary" />
            </div>
          )}
          {!managersLoading && managersError && (
            <Alert variant="danger" className="mt-3">{managersError}</Alert>
          )}
          {!managersLoading && !managersError && (
            managers.length > 0 ? (
              <Row className="mb-4">
                <Col md={12} className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                      Managers
                    </h6>
                    <span className="text-muted small">{managers.length}</span>
                  </div>
                  <Row xs={1} sm={3} md={5} className="g-2">
                    {managers.map((m) => {
                      const totalNetSum = commissions.reduce((acc, c) => acc + Number(c.total_net || 0), 0);
                      const commissionOnTotalNet = Number(m.base_commission || 0) * totalNetSum;
                      return (
                        <Col key={`manager-${m.id || m.name}`}>
                          <div className="agent-card p-3" aria-label={`Manager ${m.name}`}>
                            {/* Header */}
                            <div className="d-flex justify-content-between align-items-center mb-4">
                              <div className="d-flex align-items-center gap-3">
                                <div className="avatar-gradient">
                                  <i className="fas fa-fw fa-user-tie text-white fs-5"></i>
                                </div>
                                <div>
                                  <h6 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                                    {m.name}
                                  </h6>
                                </div>
                              </div>
                            </div>

                            {/* Body */}
                            <div className="card-body p-0">
                              <div className="commission-box">
                                <p className="small mb-1">Base Commission</p>
                                <p className="fw-bold fs-6 mb-0">
                                  {typeof m.base_commission === 'number' ? `${(m.base_commission * 100).toFixed(2)}%` : m.base_commission}
                                </p>
                              </div>

                              <div className="small text-muted">
                                <div className="d-flex justify-content-between py-1 border-top">
                                  <span>Commissions</span>
                                  <span className="fw-semibold text-dark">{formatCurrency(commissionOnTotalNet)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Col>
              </Row>
            ) : (
              <div className="text-muted small">No manager data available.</div>
            )
          )}
        </Tab>
        <Tab eventKey="processors" title={<span>Processors <span className="text-muted small">({processors.length})</span></span>}>
          {/* Totals bar */}
          {processors.length > 0 && (
            <div className="d-flex flex-wrap gap-4 mb-3 small text-muted">
              <span className="d-flex align-items-center gap-2">
                Total Closings: <span className="fw-semibold text-dark">{processors.reduce((acc, p) => acc + Number(p.closings_count ?? 0), 0)}</span>
              </span>
              <span className="d-flex align-items-center gap-2">
                Total Commissions: <span className="fw-semibold text-dark">{formatCurrency(processors.reduce((acc, p) => acc + Number(p.commission_by_files_count || 0), 0))}</span>
              </span>
            </div>
          )}
          {processors.length === 0 ? (
            <div className="text-muted small">No processor data available.</div>
          ) : (
            <Row xs={1} sm={3} md={5} className="g-2">
              {processors.map((p) => (
                <Col key={`processor-${p.id || p.name}`}>
                  <div className="agent-card p-3" aria-label={`Processor ${p.name}`}>
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <div className="d-flex align-items-center gap-3">
                        <div className="avatar-gradient">
                          <i className="fas fa-fw fa-user-tie text-white fs-5"></i>
                        </div>
                        <div>
                          <h6 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                            {p.name}
                          </h6>
                        </div>
                      </div>
                    </div>

                    <hr className="my-2" />

                    {/* Body */}
                    <div className="card-body p-0">
                      <div className="commission-box">
                        <p className="small mb-1">Base Rate</p>
                        <p className="fw-bold fs-6 mb-0">
                          {p.base_rate ? `$${p.base_rate}` : 'N/A'}
                        </p>
                      </div>

                      <div className="small text-muted">
                        <div className="d-flex justify-content-between py-1 border-top">
                          <span>Total Closings</span>
                          <span className="fw-semibold text-dark">{p.closings_count ?? 0}</span>
                        </div>
                        <div className="d-flex justify-content-between py-1 border-top">
                          <span>Commission</span>
                          <span className="fw-semibold text-dark">{formatCurrency(p.commission_by_files_count)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </Tab>
        <Tab eventKey="coordinators" title={<span>Transaction Coordinators <span className="text-muted small">({transactionCoordinators.length})</span></span>}>
          {/* Totals bar */}
          {transactionCoordinators.length > 0 && (
            <div className="d-flex flex-wrap gap-4 mb-3 small text-muted">
              <span className="d-flex align-items-center gap-2">
                Total Closings: <span className="fw-semibold text-dark">{transactionCoordinators.reduce((acc, tc) => acc + Number(tc.closings_count ?? 0), 0)}</span>
              </span>
              <span className="d-flex align-items-center gap-2">
                Total Agent Premium: <span className="fw-semibold text-dark">{formatCurrency(transactionCoordinators.reduce((acc, tc) => acc + Number(tc.total_net_sum || 0), 0))}</span>
              </span>
              <span className="d-flex align-items-center gap-2">
                Total Commissions: <span className="fw-semibold text-dark">{formatCurrency(transactionCoordinators.reduce((acc, tc) => acc + (typeof tc.commission_on_total_net === 'number' ? tc.commission_on_total_net : Number(tc.commission_transaction_coordinator || tc.commission_on_total_net || 0)), 0))}</span>
              </span>
            </div>
          )}
          {transactionCoordinators.length === 0 ? (
            <div className="text-muted small">No transaction coordinator data available.</div>
          ) : (
            <Row xs={1} sm={3} md={5} className="g-2">
              {transactionCoordinators.map((tc) => (
                <Col key={`tc-${tc.id || tc.name}`}>
                  <div className="agent-card p-3" aria-label={`Transaction Coordinator ${tc.name}`}>
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <div className="d-flex align-items-center gap-3">
                        <div className="avatar-gradient">
                          <i className="fas fa-fw fa-user text-white fs-5"></i>
                        </div>
                        <div>
                          <h6 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                            {tc.name}
                          </h6>
                        </div>
                      </div>
                    </div>

                    <hr className="my-2" />

                    {/* Body */}
                    <div className="card-body p-0">
                      <div className="commission-box">
                        <p className="small mb-1">Percentage Applied</p>
                        <p className="fw-bold fs-6 mb-0">
                          {tc.base_rate ? `${(tc.base_rate * 100).toFixed(2)}%` : 'N/A'}
                        </p>
                      </div>

                      <div className="small text-muted">
                        <div className="d-flex justify-content-between py-1 border-top">
                          <span>Total Closings</span>
                          <span className="fw-semibold text-dark">{tc.closings_count ?? 0}</span>
                        </div>
                        <div className="d-flex justify-content-between py-1 border-top">
                          <span>Total Closing Amount</span>
                          <span className="fw-semibold text-dark">{formatCurrency(tc.total_net_sum)}</span>
                        </div>
                        <div className="d-flex justify-content-between py-1 border-top">
                          <span>Commissions</span>
                           <span className="fw-semibold text-dark">${typeof tc.commission_on_total_net === 'number' ? tc.commission_on_total_net.toLocaleString() : (tc.commission_transaction_coordinator ?? tc.commission_on_total_net)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </Tab>
      </Tabs>
            </Card.Body>
          </div>
        </Collapse>
      </Card>
      <Card className="main-card">
        <Card.Header className="card-header-custom d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Commission Records</h5>
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-danger"
              onClick={handleOpenGenerateModal}
              className="d-flex align-items-center gap-2 generate-report-btn"
              aria-label="Generate Report"
            >
              Generate Report
            </Button>
            <Button
              variant="outline-light"
              onClick={handleExportCsv}
              className="d-flex align-items-center gap-2 export-csv-btn"
              aria-label="Export CSV"
              disabled={exportingCsv}
            >
              {exportingCsv ? (
                <>
                  <Spinner animation="border" size="sm" /> Exporting...
                </>
              ) : (
                <>
                  <i className="fas fa-file-csv"></i> Export CSV
                </>
              )}
            </Button>
          </div>
        </Card.Header>
        {/* <Card.Body> */}
          {loading && (
            <div className="loading-container">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading commissions...</p>
            </div>
          )}

          {error && (
            <Alert variant="danger">{error}</Alert>
          )}

          {!loading && !error && commissions.length === 0 && (
            <div className="text-center text-muted py-5">No commission records found for the selected period.</div>
          )}
        {/* </Card.Body> */}

          {!loading && !error && commissions.length > 0 && (
            <div className="table-scroll" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <Table hover bordered={false} className="table-modern" style={{ display: 'inline-table', width: 'auto', tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    <th>Order Number</th>
                    <th>Sales Persons</th>
                    <th>Allocation</th>
                    <th>Transaction Type</th>
                    <th>Commission per Sales person</th>
                    <th>OverAll Commission</th>
                    <th>Agent Policy Premium</th>
                    <th>Purchase Price</th>
                    <th>Loan Amount</th>
                    <th>Order Open Date</th>
                    <th>Order Closed Date</th>
                    <th>Address</th>
                    <th>Transaction Coordinator</th>
                    <th>Processor</th>
                    <th>Underwriter Name</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((c) => (
                    <tr key={c.id || c.order_number}>
                      <td>{c.order_number || 'N/A'}</td>
                      <td>{getSalesPersonNames(c)}</td>
                      <td>{formatAllocation(c)}</td>
                      <td>{c.transaction_type || 'N/A'}</td>
                      <td>
                        {Array.isArray(c.salesperson_entries) && c.salesperson_entries.length > 0 ? (
                          <div className="d-flex flex-column gap-2">
                            {c.salesperson_entries.map((sp, idx) => (
                              <div key={`${c.id || c.order_number}-sp-${idx}`} className="d-flex flex-column">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                  {(Number(c.sales_person_count) || 0) > 1 && (
                                    <span className="fw-semibold">{sp.sales_person_name || sp.name || 'Sales Person'}</span>
                                  )}
                                  <span className="fw-bold">{formatCurrency(sp.commission_amount)}</span>
                                  <Badge bg={sp.is_bonus_applied ? 'success' : 'secondary'} pill>
                                    {formatPercent(sp.commission_rate)}
                                  </Badge>
                                  {/* {typeof sp.is_bonus_applied === 'boolean' && ( */}
                                    { sp.is_bonus_applied == true && (
                                    <Badge bg={sp.is_bonus_applied ? 'success' : 'secondary'} pill>
                                      {sp.is_bonus_applied ? 'HiLi' : 'No bonus'}
                                    </Badge>)
                                    }
                                  {/* )} */}
                                </div>
                                <div className="text-muted small d-flex flex-wrap align-items-center gap-2">
                                  {sp.base_rate !== undefined && (
                                    <span className="text-secondary">Base {formatPercent(sp.base_rate)}</span>
                                  )}
                                  {sp.bonus_rate !== undefined && (
                                    <span className="text-primary">HiLi {formatPercent(sp.bonus_rate)}</span>
                                  )}
                                  {sp.threshold_value !== undefined && sp.threshold_value !== null && (
                                    <span className="text-dark">• Threshold {formatCurrency(sp.threshold_value)}</span>
                                  )}
                                  
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : Array.isArray(c.sales_person_data) && c.sales_person_data.length > 0 ? (
                          <div className="d-flex flex-column gap-2">
                            {c.sales_person_data.map((sp, idx) => (
                              <div key={`${c.id || c.order_number}-spd-${idx}`} className="d-flex flex-column">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                  {(Number(c.sales_person_count) || 0) > 1 && (
                                    <span className="fw-semibold">{sp.sales_person_name || sp.name || 'Sales Person'}</span>
                                  )}
                                  {sp.commission_amount !== undefined && (
                                    <span className="fw-bold">{formatCurrency(sp.commission_amount)}</span>
                                  )}
                                  {sp.commission_rate !== undefined && (
                                    <Badge bg={sp.is_bonus_applied ? 'success' : 'secondary'} pill>
                                      {formatPercent(sp.commission_rate)}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-muted small d-flex flex-wrap align-items-center gap-2">
                                  {sp.base_rate !== undefined && (
                                    <span className="text-secondary">Base {formatPercent(sp.base_rate)}</span>
                                  )}
                                  {sp.bonus_rate !== undefined && (
                                    <span className="text-primary">HiLi {formatPercent(sp.bonus_rate)}</span>
                                  )}
                                  {sp.threshold_value !== undefined && sp.threshold_value !== null && (
                                    <span className="text-warning">• Threshold {formatCurrency(sp.threshold_value)}</span>
                                  )}
                                  {typeof sp.is_bonus_applied === 'boolean' && (
                                    <Badge bg={sp.is_bonus_applied ? 'success' : 'secondary'} pill>
                                      {sp.is_bonus_applied ? 'HiLi applied' : 'No bonus'}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          // Fallback to legacy aggregate value
                          <span>{formatCurrency(c.commission_per_sales_person)}</span>
                        )}
                      </td>
                      <td>{formatCurrency(c.commission)}</td>
                      <td>{formatCurrency(c.total_net)}</td>
                      <td>{formatCurrency(c.purchase_price)}</td>
                      <td>{formatCurrency(c.loan_amount)}</td>
                      
                      <td>{formatDate(c.order_open_date)}</td>
                      <td>{formatDate(c.order_closed_date)}</td>
                      <td>{formatAddress(c)}</td>
                      <td>{c.transaction_coordinator || 'N/A'}</td>
                      <td>{c.processor || 'N/A'}</td>
                      <td>{c.underwriter_name || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        <Card.Footer className="d-flex justify-content-between align-items-center">
          <div className="pagination-info text-muted">Showing page {currentPage} of {totalPages} • Total {totalCount} records</div>
          {renderPagination()}
        </Card.Footer>
      </Card>
    </Container>
  );
};

export default FileCommissions;